import StyledButton from '@/components/common/styledButton';
import { useCreateAccountGroupMutation } from '@/store/accountsApi';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';

interface CreateAccountGroupFormProps {
  onCancel: () => void;
}

export const CreateAccountGroupForm = ({
  onCancel,
}: CreateAccountGroupFormProps) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [options, setOptions] = useState(['', '']);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [createAccountGroup, { isLoading }] = useCreateAccountGroupMutation();

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  const handleAddRows = () => {
    setOptions([...options, '']);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleFinish = async () => {
    try {
      await createAccountGroup({
        name: groupName,
        description: groupDescription,
        options: options.filter((o) => o.trim() !== ''),
      }).unwrap();
      onCancel();
    } catch (error) {
      console.error(t('createAccountGroup.failedToCreate'), error);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 1:
        return (
          <Box>
            <Typography
              variant='body1'
              color='text.primary'
              sx={{ mb: 3 }}
            >
              {t('createAccountGroup.segmentsDescription')}
            </Typography>

            {options.map((option, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}
              >
                <TextField
                  fullWidth
                  variant='outlined'
                  placeholder={t('createAccountGroup.enterSegmentName')}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                      fontSize: '16px',
                    },
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveOption(index)}
                  aria-label={t('createAccountGroup.removeSegment')}
                >
                  <DeleteOutline />
                </IconButton>
              </Box>
            ))}

            <Button
              size='large'
              onClick={handleAddRows}
              sx={{ my: 2, textTransform: 'none' }}
            >
              {t('createAccountGroup.addAnotherSegment')}
            </Button>
          </Box>
        );
      case 0:
        return (
          <Box>
            <Typography
              variant='body1'
              sx={{
                mb: 3,
                color: theme.palette.text.primary,
                fontSize: '16px',
                fontWeight: 400,
              }}
            >
              {t('createAccountGroup.startNamingGroup')}
            </Typography>

            <TextField
              required
              fullWidth
              label={t('title')}
              placeholder={t('createAccountGroup.enterGroupTitle')}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              variant='outlined'
              sx={{
                mb: 3,
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary,
                  fontSize: '16px',
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label={t('descriptionOptional')}
              placeholder={t('createAccountGroup.enterGroupDescription')}
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              multiline
              rows={4}
              variant='outlined'
              sx={{
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary,
                  fontSize: '16px',
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const hasEnoughOptions = options.filter((o) => o.trim() !== '').length >= 2;

  return (
    <Box sx={{ p: 3 }}>
      {getStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 1 }}>
        {activeStep === 0 ? (
          <StyledButton
            size='large'
            variant='contained'
            onClick={handleNext}
            disabled={!groupName}
          >
            {t('next')}
          </StyledButton>
        ) : (
          <>
            <StyledButton
              size='large'
              variant='outlined'
              onClick={onCancel}
            >
              {t('cancel')}
            </StyledButton>
            <StyledButton
              size='large'
              variant='contained'
              onClick={handleFinish}
              disabled={!hasEnoughOptions || isLoading}
            >
              {t('save')}
            </StyledButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CreateAccountGroupForm;
